/**
 * Recherche SIRENE : module d’identification, pas un champ isolé.
 */
import { CopyButton } from "../../components/CopyButton";
import { MissTip } from "../../components/MissTip";
import { useFormatters } from "../../formats/useFormatters";
import { sireneLabel } from "./SireneBadge";
import "./siretSearch.css";
import type { SireneStatus } from "./types";

export type SiretPhase = "idle" | "scan" | "ok" | "err";

export type SiretActivity = {
  sirene_status?: SireneStatus | null;
  sirene_closed_on?: string | null;
  sirene_created_on?: string | null;
};

type SiretSearchProps = {
  value: string;
  onChange: (siret: string) => void;
  phase: SiretPhase;
  foundName?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  showMiss?: boolean;
  missText?: string;
  activity?: SiretActivity | null;
  onSearch: () => void;
};

function formatSiret(raw: string): string {
  const d = raw.replace(/\D/g, "");
  if (d.length !== 14) return raw.trim();
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 9)} ${d.slice(9)}`;
}

export function SiretSearch({
  value,
  onChange,
  phase,
  foundName,
  error,
  disabled,
  required,
  showMiss,
  missText,
  activity,
  onSearch,
}: SiretSearchProps) {
  const { day } = useFormatters();
  const scanning = phase === "scan";
  const digits = value.replace(/\D/g, "");
  const status = activity?.sirene_status ?? null;
  const tone = phase === "ok" && status === "ceased" ? "ceased" : phase === "ok" && status === "establishment_closed" ? "closed" : phase;

  let out = "Saisissez le SIRET (14 chiffres) ou la raison sociale.";
  if (scanning) out = "Interrogation du répertoire SIRENE…";
  if (phase === "err") out = error || "Aucun établissement trouvé.";

  return (
    <div className={`fx-siret fx-siret--${tone}${showMiss && !disabled ? " is-miss" : ""}`}>
      <div className="fx-siret__head">
        <span className="fx-siret__mark" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect x="4" y="3.5" width="16" height="17" rx="2.2" stroke="currentColor" strokeWidth="1.6" />
            <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </span>
        <div>
          <p className="fx-siret__title">
            Répertoire SIRENE
            {required ? <span className="fx-field__req" aria-hidden /> : null}
          </p>
          <p className="fx-siret__sub">Identification officielle (INSEE)</p>
        </div>
      </div>
      <div className="fx-siret__combo-wrap">
        <div className="fx-siret__combo">
          <input
            className="fx-siret__input"
            name="siret"
            inputMode="numeric"
            autoComplete="off"
            placeholder="SIRET ou raison sociale"
            value={value}
            maxLength={64}
            disabled={scanning}
            aria-label="SIRET ou raison sociale"
            aria-required={required || undefined}
            aria-invalid={showMiss && !disabled ? true : undefined}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onSearch();
              }
            }}
          />
          {digits.length === 14 ? <CopyButton text={digits} label="Copier le SIRET" /> : null}
          <button type="button" className="fx-siret__btn" disabled={disabled} onClick={onSearch}>
            {scanning ? <span className="fx-siret__spin" aria-hidden="true" /> : null}
            {scanning ? "Recherche…" : "Rechercher"}
          </button>
        </div>
        {showMiss && !disabled ? <MissTip text={missText || "SIRET à 14 chiffres pour enregistrer."} /> : null}
      </div>
      {phase === "ok" ? (
        <p className="fx-siret__out" aria-live="polite">
          Établissement identifié
          {foundName ? (
            <>
              {" — "}
              <strong>{foundName}</strong>
            </>
          ) : null}
          {digits.length === 14 ? ` · ${formatSiret(value)}` : null}
          {status ? (
            <>
              {" · "}
              <strong>{sireneLabel(status)}</strong>
              {status === "active" && activity?.sirene_created_on
                ? ` depuis le ${day(activity.sirene_created_on)}`
                : null}
              {status === "ceased" ? " — ne pas facturer sans relire." : null}
              {status === "establishment_closed" ? " — cet établissement est fermé." : null}
            </>
          ) : null}
        </p>
      ) : (
        <p className="fx-siret__out" aria-live="polite">
          {out}
        </p>
      )}
    </div>
  );
}
